import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, ArrowLeft, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import PasswordRequiredModal from './PasswordRequiredModal';
import { getBackendErrorMessage } from '../utils/toastError';
import useEscapeKey from '../hooks/useEscapeKey';

const WALLET_ADDRESS_LENGTH = 36;

const SendFiresModal = ({ isOpen, onClose, currentBalance, onSuccess, onBack }) => {
  useEscapeKey(onClose, isOpen);
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form', 'confirm', or 'password'
  const [formData, setFormData] = useState({
    to_wallet_id: '',
    amount: ''
  });

  const [clientOperationId, setClientOperationId] = useState('');

  const generateClientOpId = useMemo(() => {
    return () => {
      try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID();
        }
      } catch (e) {}
      return `op_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    };
  }, []);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [storeLookup, setStoreLookup] = useState({
    checked: false,
    isStore: false,
    storeName: null
  });

  const isNumericWallet = /^\d+$/.test(formData.to_wallet_id.trim());
  const isFullAddress =
    (isNumericWallet && formData.to_wallet_id.trim().length > 0) ||
    (!isNumericWallet && formData.to_wallet_id.trim().length === WALLET_ADDRESS_LENGTH);

  const commissionRate = storeLookup.isStore ? 0 : 0.05;
  const commission = formData.amount
    ? (parseFloat(formData.amount) * commissionRate).toFixed(2)
    : '0.00';
  const total = formData.amount
    ? (parseFloat(formData.amount) + parseFloat(commission)).toFixed(2)
    : '0.00';

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'to_wallet_id'
      ? value.slice(0, WALLET_ADDRESS_LENGTH)
      : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Detectar si la wallet destino es de tienda (sin comisión)
  useEffect(() => {
    const wallet = formData.to_wallet_id.trim();
    if (!wallet || (!/^\d+$/.test(wallet) && wallet.length !== WALLET_ADDRESS_LENGTH)) {
      setStoreLookup({ checked: false, isStore: false, storeName: null });
      return;
    }

    let cancelled = false;
    const lookup = async () => {
      try {
        const res = await axios.get(`/api/economy/wallet-lookup/${wallet}`);
        if (cancelled) return;
        setStoreLookup({
          checked: true,
          isStore: !!res.data?.is_store_wallet,
          storeName: res.data?.store_name || null
        });
      } catch (err) {
        if (cancelled) return;
        setStoreLookup({ checked: true, isStore: false, storeName: null });
      }
    };

    lookup();
    return () => {
      cancelled = true;
    };
  }, [formData.to_wallet_id]);

  const storeBadgeText = (() => {
    if (!storeLookup.checked || !storeLookup.isStore) return null;
    return `Billetera de tienda: ${storeLookup.storeName || 'Tienda'} (comisión 0%)`;
  })();

  const handlePaste = async () => {
    try {
      // Primero intentar API moderna de clipboard
      if (navigator.clipboard && navigator.clipboard.readText) {
        try {
          const text = await navigator.clipboard.readText();
          if (text && text.trim()) {
            setFormData(prev => ({
              ...prev,
              to_wallet_id: text.trim().slice(0, WALLET_ADDRESS_LENGTH)
            }));
            toast.success('Dirección pegada');
            return;
          }
        } catch (clipboardError) {
          // Si falla, continuar con método alternativo
          console.log('Clipboard API fallida, usando método alternativo');
        }
      }

      // Método alternativo: usar execCommand (funciona en más dispositivos)
      const input = document.querySelector('input[name="to_wallet_id"]');
      if (input) {
        // Enfocar el input
        input.focus();
        input.select();
        
        // Intentar ejecutar paste command
        const success = document.execCommand('paste');
        
        if (success) {
          // Dar un momento para que el navegador pegue
          setTimeout(() => {
            if (input.value && input.value.trim()) {
              toast.success('Dirección pegada');
            }
          }, 100);
        } else {
          // Si execCommand no funciona, mostrar ayuda
          toast('Mantén presionado y selecciona "Pegar"', {
            icon: '👆',
            duration: 2500
          });
        }
      }
    } catch (error) {
      console.error('Paste error:', error);
      // Último recurso: guiar al usuario
      const input = document.querySelector('input[name="to_wallet_id"]');
      input?.focus();
      toast('Toca el campo y pega manualmente', {
        icon: '✍️',
        duration: 2500
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.to_wallet_id) {
      newErrors.to_wallet_id = 'Dirección de billetera requerida';
    } else if (!isNumericWallet && formData.to_wallet_id.length !== WALLET_ADDRESS_LENGTH) {
      const remaining = WALLET_ADDRESS_LENGTH - formData.to_wallet_id.length;
      newErrors.to_wallet_id = remaining > 0
        ? `Faltan ${remaining} caracteres para completar la dirección`
        : 'La dirección debe tener exactamente 36 caracteres';
    }

    if (!formData.amount) {
      newErrors.amount = 'Monto requerido';
    } else {
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 1) {
        newErrors.amount = 'Mínimo 1 fuego';
      } else if (amount > 10000) {
        newErrors.amount = 'Máximo 10,000 fuegos';
      } else if (parseFloat(total) > currentBalance) {
        newErrors.amount = `Balance insuficiente (tienes ${currentBalance} fuegos)`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      setShowPasswordModal(true);
    }
  };

  const handlePasswordConfirmed = () => {
    setShowPasswordModal(false);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const opId = clientOperationId || generateClientOpId();
      if (!clientOperationId) setClientOperationId(opId);

      const response = await axios.post('/api/economy/transfer', {
        ...formData,
        client_operation_id: opId
      });
      toast.success(`${formData.amount} fuegos enviados exitosamente`);
      
      // Invalidar queries para actualizar datos en tiempo real
      await refreshUser();
      queryClient.invalidateQueries(['user-stats']);
      queryClient.invalidateQueries(['user-wallet']);
      queryClient.invalidateQueries(['wallet-transactions']);
      
      onSuccess && onSuccess(response.data);
      handleClose();
    } catch (error) {
      toast.error(getBackendErrorMessage(error, 'Error al enviar fuegos'));
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ to_wallet_id: '', amount: '' });
    setClientOperationId('');
    setErrors({});
    setStep('form');
    setShowPasswordModal(false);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md card-glass p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (step === 'confirm') {
                      setStep('form');
                    } else {
                      if (onBack) {
                        onBack();
                      } else {
                        handleClose();
                        navigate('/profile?tab=fires');
                      }
                    }
                  }}
                  className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-full bg-fire-orange/20 flex items-center justify-center">
                  <Send size={20} className="text-fire-orange" />
                </div>
                <h2 className="text-xl font-bold">
                  {step === 'form' ? 'Enviar Fuegos' : 'Confirmar Envío'}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Form Step */}
            {step === 'form' && (
              <div className="space-y-4">
                {/* Wallet Address */}
                <div>
                  <label className="block text-sm font-medium text-text/80 mb-2">
                    Dirección de Billetera Destino
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="to_wallet_id"
                      value={formData.to_wallet_id}
                      onChange={handleChange}
                      maxLength={WALLET_ADDRESS_LENGTH}
                      onPaste={(e) => {
                        // Permitir pegado nativo y mostrar confirmación
                        setTimeout(() => {
                          if (e.target.value && e.target.value.trim()) {
                            const trimmed = e.target.value.trim().slice(0, WALLET_ADDRESS_LENGTH);
                            setFormData(prev => ({ ...prev, to_wallet_id: trimmed }));
                            toast.success('Dirección pegada', { duration: 1500 });
                          }
                        }, 50);
                      }}
                      className={`input-glass flex-1 ${errors.to_wallet_id ? 'border-red-500' : ''}`}
                      placeholder="Pega la dirección aquí"
                    />
                    <button
                      type="button"
                      onClick={handlePaste}
                      className="px-4 py-2 btn-secondary text-xs"
                    >
                      Pegar
                    </button>
                  </div>
                  {errors.to_wallet_id && (
                    <p className="text-xs text-red-400 mt-1">{errors.to_wallet_id}</p>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-text/80 mb-2">
                    Cantidad de Fuegos
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`input-glass w-full ${errors.amount ? 'border-red-500' : ''}`}
                    placeholder="1 - 10,000"
                    min="1"
                    max="10000"
                    step="0.01"
                  />
                  {errors.amount && (
                    <p className="text-xs text-red-400 mt-1">{errors.amount}</p>
                  )}
                  <p className="text-xs text-text-secondary mt-1">
                    Balance disponible: <strong className="text-text">{currentBalance} 🔥</strong>
                  </p>
                </div>

                {/* Fee Info */}
                {formData.amount && parseFloat(formData.amount) > 0 && (
                  <div className="p-3.5 rounded-xl bg-surface-1 border border-theme space-y-2 text-sm">
                    {storeLookup.checked && storeLookup.isStore && (
                      <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 font-medium">
                        <span>
                          {storeBadgeText}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Monto a enviar:</span>
                      <span className="text-primary font-semibold">{formData.amount} 🔥</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">
                        Comisión ({(commissionRate * 100).toFixed(0)}%{storeLookup.isStore ? ' - tienda' : ''}):
                      </span>
                      <span className="text-amber-800 font-bold">{commission} 🔥</span>
                    </div>
                    <div className="border-t border-theme pt-2 flex justify-between font-bold">
                      <span className="text-text">Total a descontar:</span>
                      <span className="text-fire-orange">{total} 🔥</span>
                    </div>
                  </div>
                )}

                {/* Continue Button */}
                <button
                  onClick={handleNext}
                  className="w-full btn-primary"
                >
                  Continuar
                </button>
              </div>
            )}

            {/* Confirmation Step */}
            {step === 'confirm' && (
              <div className="space-y-4">
                {/* Warning */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                  <AlertCircle size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-bold text-amber-900 mb-1">Confirma los detalles</p>
                    <p className="text-amber-800 text-xs">
                      Esta acción no se puede deshacer. Verifica que la dirección sea correcta.
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 rounded-xl bg-surface-1 border border-theme space-y-3">
                  <div>
                    <label className="text-xs text-text-secondary block mb-1 font-semibold">Enviar a:</label>
                    <p className="text-sm font-mono text-primary font-bold break-all">
                      {formData.to_wallet_id}
                    </p>
                  </div>

                  <div className="border-t border-theme pt-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Monto:</span>
                      <span className="text-primary font-semibold">{formData.amount} 🔥</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">
                        Comisión ({(commissionRate * 100).toFixed(0)}%{storeLookup.isStore ? ' - tienda' : ''}):
                      </span>
                      <span className="text-amber-800 font-bold">{commission} 🔥</span>
                    </div>
                    <div className="flex justify-between text-base font-bold">
                      <span className="text-text">Total:</span>
                      <span className="text-fire-orange">{total} 🔥</span>
                    </div>
                  </div>
                </div>

                {/* Confirm Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep('form')}
                    className="flex-1 btn-secondary"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    {loading ? 'Enviando...' : 'Confirmar Envío'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    <PasswordRequiredModal
      isOpen={showPasswordModal}
      onClose={() => setShowPasswordModal(false)}
      onSuccess={handlePasswordConfirmed}
      action="enviar fuegos"
    />
  </>
  );
};

export default SendFiresModal;
